import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import api from '../api/axios'
import { Check, RotateCcw, Lock, Mail, Plus, Trash2, Wifi, WifiOff, Pencil, AlertTriangle, Eye, EyeOff, Activity, RefreshCw, ChevronDown, ChevronUp, Zap, CheckCircle, XCircle } from 'lucide-react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'

function Toggle({ value, onChange }: { value: boolean, onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!value)}
      className={`relative w-10 h-[22px] rounded-full transition-colors flex-shrink-0 ${value ? 'bg-orange-500' : 'bg-gray-200'}`}
    >
      <span className={`absolute top-[3px] w-4 h-4 bg-white rounded-full shadow-sm transition-all ${value ? 'left-[3px]' : 'left-[19px]'}`} />
    </button>
  )
}

export default function SettingsPage() {
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [values, setValues] = useState<Record<string, string>>({})
  const [saved, setSaved] = useState<Record<string, boolean>>({})
  const [saving, setSaving] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'general' | 'mail' | 'roles' | 'account' | 'danger' | 'services'>('general')

  useEffect(() => {
    api.get('/settings').then(res => {
      setCategories(res.data)
      const initial: Record<string, string> = {}
      res.data.forEach((cat: any) => {
        cat.settings.forEach((s: any) => { initial[s.settingKey] = s.settingValue })
      })
      setValues(initial)
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  const handleSave = async (key: string) => {
    setSaving(key)
    try {
      await api.patch(`/settings/${key}`, { value: values[key] })
      setSaved(s => ({ ...s, [key]: true }))
      setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 2000)
    } catch {}
    setSaving(null)
  }

  const renderInput = (setting: any) => {
    if (setting.isProtected) {
      return (
        <div className="flex items-center gap-2 text-gray-400">
          <Lock size={13} />
          <span className="text-sm font-mono">{setting.settingValue}</span>
        </div>
      )
    }

    // Settings that should be dropdowns
    const dropdownOptions: Record<string, { label: string, value: string }[]> = {
      qc_credit_mode: [
        { label: 'Per Job — QC earns one credit per job reviewed', value: 'per_job' },
        { label: 'Per Hour — QC earns credits based on time spent', value: 'per_hour' },
        { label: 'Flat Rate — fixed credit regardless of job size', value: 'flat_rate' }
      ],
      manual_review_sensitivity: [
        { label: 'Low — only flag obvious issues', value: 'low' },
        { label: 'Medium — flag moderate ambiguities', value: 'medium' },
        { label: 'High — flag anything uncertain', value: 'high' }
      ],
      carryover_credit_weight: [
        { label: '25% — time worked counts at 25% of normal rate', value: '0.25' },
        { label: '50% — time worked counts at 50% of normal rate', value: '0.50' },
        { label: '75% — time worked counts at 75% of normal rate', value: '0.75' },
        { label: '100% — time worked counts at full normal rate', value: '1.00' }
      ],
      default_request_type: [
        { label: 'Fresh — new original work', value: 'fresh' },
        { label: 'Revision — rework of existing job', value: 'revision' },
        { label: 'Correction — fix QC-flagged issues', value: 'correction' }
      ],
      night_shift_cutoff: [
        { label: '05:00 — 5:00 AM', value: '05:00' },
        { label: '06:00 — 6:00 AM', value: '06:00' },
        { label: '07:00 — 7:00 AM', value: '07:00' },
        { label: '08:00 — 8:00 AM', value: '08:00' },
      ]
    }

    if (dropdownOptions[setting.settingKey]) {
      const options = dropdownOptions[setting.settingKey]
      return (
        <div className="flex items-center gap-3">
          {saved[setting.settingKey] && (
            <span className="text-xs text-green-500 flex items-center gap-1"><Check size={11} /> Saved</span>
          )}
          <select
            value={values[setting.settingKey] || ''}
            onChange={e => {
              setValues(v => ({ ...v, [setting.settingKey]: e.target.value }))
              api.patch(`/settings/${setting.settingKey}`, { value: e.target.value }).then(() => {
                setSaved(s => ({ ...s, [setting.settingKey]: true }))
                setTimeout(() => setSaved(s => ({ ...s, [setting.settingKey]: false })), 1500)
              })
            }}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-72 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
          >
            {options.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )
    }

    if (setting.dataType === 'boolean') {
      return (
        <div className="flex items-center gap-3">
          {saved[setting.settingKey] && (
            <span className="text-xs text-green-500 flex items-center gap-1"><Check size={11} /> Saved</span>
          )}
          <Toggle
            value={values[setting.settingKey] === 'true'}
            onChange={v => {
              setValues(prev => ({ ...prev, [setting.settingKey]: String(v) }))
              api.patch(`/settings/${setting.settingKey}`, { value: String(v) }).then(() => {
                setSaved(s => ({ ...s, [setting.settingKey]: true }))
                setTimeout(() => setSaved(s => ({ ...s, [setting.settingKey]: false })), 1500)
              })
            }}
          />
        </div>
      )
    }

    if (setting.dataType === 'number') {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={values[setting.settingKey] || ''}
            onChange={e => setValues(v => ({ ...v, [setting.settingKey]: e.target.value }))}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-28 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all font-mono"
          />
          <button
            onClick={() => handleSave(setting.settingKey)}
            disabled={saving === setting.settingKey}
            className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs px-3 py-2 rounded-xl transition-all font-medium"
          >
            {saving === setting.settingKey ? '...' : saved[setting.settingKey] ? '✓ Saved' : 'Save'}
          </button>
        </div>
      )
    }

    return (
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={values[setting.settingKey] || ''}
          onChange={e => setValues(v => ({ ...v, [setting.settingKey]: e.target.value }))}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm w-48 focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition-all"
        />
        <button
          onClick={() => handleSave(setting.settingKey)}
          disabled={saving === setting.settingKey}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs px-3 py-2 rounded-xl transition-all font-medium"
        >
          {saving === setting.settingKey ? '...' : saved[setting.settingKey] ? '✓ Saved' : 'Save'}
        </button>
      </div>
    )
  }

  const tabs = [
    { key: 'general', label: 'General' },
    { key: 'mail', label: 'Mail' },
    { key: 'roles', label: 'Roles' },
    { key: 'account', label: 'Account' },
    { key: 'services', label: 'Services' },
    { key: 'danger', label: 'Danger Zone' },
  ]

return (
    <div className="p-4 sm:p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-navy-900">Settings</h1>
        <p className="text-gray-400 mt-1">Configure portal behaviour and rules</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 dark:bg-[#112240] rounded-2xl p-1 mb-6 overflow-x-auto">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as any)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${activeTab === tab.key ? 'bg-white dark:bg-[#1c3a5e] text-navy-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-navy-700 dark:hover:text-gray-300'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="card p-20 text-center text-gray-400">Loading...</div>
      ) : (
        <div className="space-y-6">
          {/* General Tab */}
          {activeTab === 'general' && <>
            {categories.filter(cat => cat.categoryKey === 'performance').map(cat => (
              <div key={cat.categoryKey} className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-8 h-8 bg-navy-50 rounded-lg flex items-center justify-center">
                    <span className="text-navy-600 text-sm">⚙</span>
                  </div>
                  <div>
                    <h2 className="font-display font-semibold text-navy-900">{cat.label}</h2>
                    <p className="text-xs text-gray-400">{cat.description}</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {cat.settings.filter((s: any) => !s.settingKey.startsWith('noreply_smtp_')).map((setting: any) => (
                    <div key={setting.settingKey} className="px-6 py-4 flex items-center justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-navy-800">{setting.label}</p>
                          {setting.settingKey === 'weekly_usage_email_enabled' && (
                            <button onClick={async () => {
                              if (!window.confirm('Send weekly usage email to all active subscribers now?')) return
                              try {
                                await api.post('/settings/trigger/weekly-usage-email')
                                toast.success('Weekly usage emails sent successfully!')
                              } catch {
                                toast.error('Failed to send emails')
                              }
                            }}
                              className="text-[10px] bg-orange-50 text-orange-500 hover:bg-orange-100 border border-orange-200 px-2 py-0.5 rounded-lg font-medium transition-colors">
                              Send Now
                            </button>
                          )}
                          {setting.requiresRestart && <span className="bg-yellow-50 text-yellow-600 text-xs px-1.5 py-0.5 rounded-md font-medium">Restart required</span>}
                          {setting.isProtected && <span className="bg-gray-100 text-gray-400 text-xs px-1.5 py-0.5 rounded-md font-medium flex items-center gap-1"><Lock size={9} /> Protected</span>}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{setting.description}</p>
                      </div>
                      <div className="flex-shrink-0">{renderInput(setting)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {categories.filter(cat => cat.categoryKey === 'branding').map(cat => (
              <div key={cat.categoryKey} className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-8 h-8 bg-navy-50 rounded-lg flex items-center justify-center">
                    <span className="text-navy-600 text-sm">⚙</span>
                  </div>
                  <div>
                    <h2 className="font-display font-semibold text-navy-900">{cat.label}</h2>
                    <p className="text-xs text-gray-400">{cat.description}</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {cat.settings.filter((s: any) => !s.settingKey.startsWith('noreply_smtp_')).map((setting: any) => (
                    <div key={setting.settingKey} className="px-6 py-4 flex items-center justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-navy-800">{setting.label}</p>
                          {setting.requiresRestart && <span className="bg-yellow-50 text-yellow-600 text-xs px-1.5 py-0.5 rounded-md font-medium">Restart required</span>}
                          {setting.isProtected && <span className="bg-gray-100 text-gray-400 text-xs px-1.5 py-0.5 rounded-md font-medium flex items-center gap-1"><Lock size={9} /> Protected</span>}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{setting.description}</p>
                      </div>
                      <div className="flex-shrink-0">{renderInput(setting)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {categories.filter(cat => ['workflow', 'mail', 'notifications', 'security'].includes(cat.categoryKey)).map(cat => (
              <div key={cat.categoryKey} className="card overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className="w-8 h-8 bg-navy-50 rounded-lg flex items-center justify-center">
                    <span className="text-navy-600 text-sm">⚙</span>
                  </div>
                  <div>
                    <h2 className="font-display font-semibold text-navy-900">{cat.label}</h2>
                    <p className="text-xs text-gray-400">{cat.description}</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {cat.settings.filter((s: any) => !s.settingKey.startsWith('noreply_smtp_')).map((setting: any) => (
                    <div key={setting.settingKey} className="px-6 py-4 flex items-center justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-navy-800">{setting.label}</p>
                          {setting.requiresRestart && <span className="bg-yellow-50 text-yellow-600 text-xs px-1.5 py-0.5 rounded-md font-medium">Restart required</span>}
                          {setting.isProtected && <span className="bg-gray-100 text-gray-400 text-xs px-1.5 py-0.5 rounded-md font-medium flex items-center gap-1"><Lock size={9} /> Protected</span>}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{setting.description}</p>
                      </div>
                      <div className="flex-shrink-0">{renderInput(setting)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>}

          {/* Mail Tab */}
          {activeTab === 'mail' && <>
            <MasterMailSection />
            <MailAccountsSection />
            <MailTemplatesSection />
            <NoReplySmtpSection />
            <NotificationEmailsSection />
          </>}

          {/* Roles Tab */}
          {activeTab === 'roles' && <RoleManagementSection />}

          {/* Account Tab */}
          {activeTab === 'account' && <>
            <NotificationPreferencesSection />
            <ChangePasswordSection />
          </>}

          {/* Services Tab */}
          {activeTab === 'services' && <GeminiStatusSection />}

          {/* Danger Zone Tab */}
          {activeTab === 'danger' && <DangerZoneSection />}
        </div>
      )}
    </div>
  )
}

function NotificationEmailsSection() {
  const KEYS = ['admin_notification_email', 'trial_signup_notification_email']
  const [form, setForm] = useState({ admin_notification_email: '', trial_signup_notification_email: '' })
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    api.get('/settings').then(res => {
      const allSettings: any[] = []
      res.data.forEach((cat: any) => allSettings.push(...cat.settings))
      const updated: any = { ...form }
      KEYS.forEach(key => {
        const s = allSettings.find((s: any) => s.settingKey === key)
        if (s) updated[key] = s.settingValue || ''
      })
      setForm(updated)
      setLoaded(true)
    }).catch(() => setLoaded(true))
  }, [])

  const handleSave = async () => {
    setSaving(true); setSaved(false)
    try {
      await Promise.all(KEYS.map(key => api.patch(`/settings/${key}`, { value: form[key as keyof typeof form] })))
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch { toast.error('Failed to save notification email settings') }
    setSaving(false)
  }

  if (!loaded) return null

  return (
    <div className="card overflow-hidden mt-6">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-blue-50 w-8 h-8 rounded-lg flex items-center justify-center">
            <Mail size={15} className="text-blue-500" />
          </div>
          <div>
            <p className="font-display font-semibold text-navy-900">Admin Notification Emails</p>
            <p className="text-xs text-gray-400">Email addresses that receive admin alerts from the portal</p>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 text-sm bg-navy-800 text-white px-4 py-2 rounded-xl hover:bg-navy-700 disabled:opacity-50 transition-colors font-medium">
          {saved ? <><Check size={14} /> Saved</> : saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="p-6 space-y-4">
        <div>
          <label className="label">Admin Notification Email</label>
          <input className="input text-sm" type="email" placeholder="ink@inkcrediblestudios.com"
            value={form.admin_notification_email} onChange={e => set('admin_notification_email', e.target.value)} />
          <p className="text-xs text-gray-400 mt-1">Receives general portal admin notifications (new signups, alerts, etc.)</p>
        </div>
        <div>
          <label className="label">Trial Signup Notification Email</label>
          <input className="input text-sm" type="email" placeholder="sam@inkcrediblestudios.com"
            value={form.trial_signup_notification_email} onChange={e => set('trial_signup_notification_email', e.target.value)} />
          <p className="text-xs text-gray-400 mt-1">CC'd on every new free trial signup email. Leave blank to skip.</p>
        </div>
      </div>
    </div>
  )
}


function NoReplySmtpSection() {
  const KEYS = ['noreply_smtp_host', 'noreply_smtp_port', 'noreply_smtp_user', 'noreply_smtp_password', 'noreply_smtp_from_name', 'noreply_smtp_from_email']

  const [form, setForm] = useState({
    noreply_smtp_host: '',
    noreply_smtp_port: '465',
    noreply_smtp_user: '',
    noreply_smtp_password: '',
    noreply_smtp_from_name: 'Inkcredible Studio',
    noreply_smtp_from_email: ''
  })
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean, message: string } | null>(null)
  const [saved, setSaved] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  useEffect(() => {
    api.get('/settings').then(res => {
      const allSettings: any[] = []
      res.data.forEach((cat: any) => allSettings.push(...cat.settings))
      const updated: any = { ...form }
      KEYS.forEach(key => {
        const s = allSettings.find((s: any) => s.settingKey === key)
        if (s) updated[key] = s.settingValue || ''
      })
      setForm(updated)
      setLoaded(true)
    }).catch(() => setLoaded(true))
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setSaved(false)
    try {
      await Promise.all(
        KEYS.map(key => api.patch(`/settings/${key}`, { value: form[key as keyof typeof form] }))
      )
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch {
      toast.error('Failed to save SMTP settings')
    }
    setSaving(false)
  }

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await api.post('/mail/test-smtp', {
        smtpHost: form.noreply_smtp_host,
        smtpPort: parseInt(form.noreply_smtp_port),
        smtpUser: form.noreply_smtp_user,
        smtpPassword: form.noreply_smtp_password
      })
      setTestResult({ success: true, message: res.data.message || 'Connection successful' })
    } catch (e: any) {
      setTestResult({ success: false, message: e.response?.data?.message || 'Connection failed' })
    }
    setTesting(false)
  }

  const isConfigured = !!(form.noreply_smtp_host && form.noreply_smtp_user && form.noreply_smtp_password)

  if (!loaded) return null

  return (
    <div className="card overflow-hidden mt-6">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-orange-50 w-8 h-8 rounded-lg flex items-center justify-center">
            <Mail size={15} className="text-orange-500" />
          </div>
          <div>
            <p className="font-display font-semibold text-navy-900">Portal Client's No-Reply Mail</p>
            <p className="text-xs text-gray-400">Used for sending completion, subscription expiry & job limit emails to clients</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConfigured ? (
            <span className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 px-2.5 py-1 rounded-full font-medium">
              <span className="w-1.5 h-1.5 bg-green-500 rounded-full" /> Configured
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
              <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" /> Not configured
            </span>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">SMTP Host</label>
            <input className="input text-sm" placeholder="smtp.gmail.com"
              value={form.noreply_smtp_host} onChange={e => set('noreply_smtp_host', e.target.value)} />
          </div>
          <div>
            <label className="label">SMTP Port</label>
            <input className="input text-sm" type="number" placeholder="465"
              value={form.noreply_smtp_port} onChange={e => set('noreply_smtp_port', e.target.value)} />
          </div>
          <div>
            <label className="label">Username</label>
            <input className="input text-sm" placeholder="noreply@inkcredible.com"
              value={form.noreply_smtp_user} onChange={e => set('noreply_smtp_user', e.target.value)} />
          </div>
          <div>
            <label className="label">Password</label>
            <div className="relative">
              <input className="input text-sm pr-10"
                type={showPassword ? 'text' : 'password'}
                placeholder="App password or SMTP password"
                value={form.noreply_smtp_password}
                onChange={e => set('noreply_smtp_password', e.target.value)} />
              <button type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword
                  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                }
              </button>
            </div>
          </div>
          <div>
            <label className="label">From Name</label>
            <input className="input text-sm" placeholder="Inkcredible Studio"
              value={form.noreply_smtp_from_name} onChange={e => set('noreply_smtp_from_name', e.target.value)} />
          </div>
          <div>
            <label className="label">From Email <span className="text-gray-400 font-normal">(if different from username)</span></label>
            <input className="input text-sm" placeholder="noreply@inkcredible.com"
              value={form.noreply_smtp_from_email} onChange={e => set('noreply_smtp_from_email', e.target.value)} />
          </div>
        </div>

        {testResult && (
          <div className={`text-xs px-3 py-2 rounded-lg font-medium ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
            {testResult.success ? '✓ ' : '✗ '}{testResult.message}
          </div>
        )}

        <div className="flex items-center gap-3 pt-1">
          <button
            onClick={handleTest}
            disabled={testing || !form.noreply_smtp_host || !form.noreply_smtp_user || !form.noreply_smtp_password}
            className="text-xs border border-gray-200 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2 transition-colors">
            <Wifi size={12} /> {testing ? 'Testing...' : 'Test Connection'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-xs bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 flex items-center gap-2 transition-colors">
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  )
}

function MailAccountsSection() {
  const [accounts, setAccounts] = useState<any[]>([])
  const [clients, setClients] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showAdd, setShowAdd] = useState(false)
  const [editAccount, setEditAccount] = useState<any>(null)
  const [editForm, setEditForm] = useState<any>({})
  const [editSaving, setEditSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean, message: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [logs, setLogs] = useState<any[]>([])
  const [showLogs, setShowLogs] = useState<string | null>(null)
  const [health, setHealth] = useState<any>(null)
  const [healthLoading, setHealthLoading] = useState(false)
  const [showHealth, setShowHealth] = useState(true)
  const [healthPage, setHealthPage] = useState(1)
  const healthPerPage = 5

  const fetchHealth = async () => {
    setHealthLoading(true)
    try {
      const res = await api.get('/mail/health')
      setHealth(res.data)
    } catch {}
    setHealthLoading(false)
  }
  const [form, setForm] = useState({
    clientId: '', email: '', imapHost: '', imapPort: '993',
    imapUser: '', imapPassword: '', useSsl: true, mailbox: 'INBOX',
    smtpHost: '', smtpPort: '465', senderName: ''
  })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  const fetchAccounts = async () => {
    setLoading(true)
    try {
      const [accs, cls] = await Promise.all([api.get('/mail'), api.get('/clients')])
      setAccounts(accs.data)
      setClients(cls.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchAccounts() }, [])

  useEffect(() => { fetchAccounts(); fetchHealth() }, [])

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await api.post('/mail/test', {
        imapHost: form.imapHost, imapPort: parseInt(form.imapPort),
        imapUser: form.imapUser, imapPassword: form.imapPassword, useSsl: form.useSsl
      })
      setTestResult({ success: true, message: res.data.message })
    } catch (e: any) {
      setTestResult({ success: false, message: e.response?.data?.message || 'Connection failed' })
    }
    setTesting(false)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.post('/mail', { ...form, imapPort: parseInt(form.imapPort) })
      setShowAdd(false)
      setForm({ clientId: '', email: '', imapHost: '', imapPort: '993', imapUser: '', imapPassword: '', useSsl: true, mailbox: 'INBOX', smtpHost: '', smtpPort: '465', senderName: '' })
      setTestResult(null)
      fetchAccounts()
    } catch (e: any) {
      console.error('Save error:', e.response?.status, e.response?.data)
      alert(e.response?.data?.error || e.message || 'Failed to save')
    }
    setSaving(false)
  }

  const handleToggle = async (id: string, isActive: boolean) => {
    await api.patch(`/mail/${id}`, { isActive: !isActive })
    fetchAccounts()
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this mail account?')) return
    await api.delete(`/mail/${id}`)
    fetchAccounts()
  }

  const handleUpdateInterval = async (id: string, minutes: number) => {
    try {
      await api.patch(`/mail/${id}`, { pollIntervalMinutes: minutes })
      fetchAccounts()
      fetchHealth()
    } catch {}
  }

  const handleViewLogs = async (id: string) => {
    setShowLogs(id)
    const res = await api.get(`/mail/logs?accountId=${id}&limit=20`)
    setLogs(res.data)
  }

  return (
    <div className="space-y-4">
    {/* Mail Health Monitor */}
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between cursor-pointer"
        onClick={() => setShowHealth(v => !v)}>
        <div className="flex items-center gap-3">
          <div className="bg-green-50 w-8 h-8 rounded-lg flex items-center justify-center">
            <Activity size={15} className="text-green-500" />
          </div>
          <div>
            <p className="font-display font-semibold text-navy-900 dark:text-white">Mail Health Monitor</p>
            <p className="text-xs text-gray-400">Real-time polling status and mail volume</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={e => { e.stopPropagation(); fetchHealth() }} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <RefreshCw size={14} className={healthLoading ? 'animate-spin' : ''} />
          </button>
          {showHealth ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </div>
      </div>
      {showHealth && (
        <div className="p-5 space-y-4">
          {healthLoading && !health ? (
            <div className="py-6 text-center text-gray-400 text-sm">Loading health data...</div>
          ) : health ? (
            <>
              {/* Per-account status table */}
              {(() => {
                const allAccounts = [
                  ...(health.masterHealth ? [{ ...health.masterHealth, isMaster: true }] : []),
                  ...health.health
                ]
                const totalPages = Math.ceil(allAccounts.length / healthPerPage)
                const pageAccounts = allAccounts.slice((healthPage - 1) * healthPerPage, healthPage * healthPerPage)
                return (
              <div className="rounded-xl border border-gray-100 dark:border-[#1e3a5f] overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 dark:bg-[#112240] border-b border-gray-100 dark:border-[#1e3a5f]">
                    <tr>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Account</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Status</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Last Polled</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Interval</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Processed</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Errors</th>
                      <th className="text-left px-4 py-2.5 font-semibold text-gray-400 uppercase tracking-wide text-[10px]">Recent</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-[#1e3a5f]">
                    {pageAccounts.map((a: any) => (
                      <tr key={a.imapUser} className={`${a.isStale ? 'bg-red-50/30 dark:bg-red-900/5' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.isStale ? 'bg-red-400 animate-pulse' : 'bg-green-400'}`} />
                            <span className="font-medium text-navy-800 dark:text-white truncate max-w-[160px]">{a.imapUser}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${a.isStale ? 'bg-red-100 text-red-600' : 'bg-green-50 text-green-600'}`}>
                            {a.isStale ? 'Stale' : 'Active'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500">
                          {a.minutesSinceLastPoll !== null ? `${a.minutesSinceLastPoll}m ago` : 'Never'}
                        </td>
                        <td className="px-4 py-3 text-gray-500">{a.pollIntervalMinutes} min</td>
                        <td className="px-4 py-3">
                          <div>
                            <span className="font-semibold text-green-600">{a.processedToday}</span>
                            <span className="text-gray-400 ml-1">today</span>
                            <span className="text-gray-300 mx-1">/</span>
                            <span className="text-gray-500">{a.processedWeek} week</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <span className={`font-semibold ${a.errorsToday > 0 ? 'text-red-500' : 'text-gray-400'}`}>{a.errorsToday}</span>
                            <span className="text-gray-400 ml-1">today</span>
                            <span className="text-gray-300 mx-1">/</span>
                            <span className="text-gray-500">{a.errorsWeek} week</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="space-y-0.5 max-w-[200px]">
                            {a.recentLogs.slice(0, 3).map((l: any, i: number) => (
                              <div key={i} className="flex items-center gap-1">
                                <span className={`font-bold flex-shrink-0 ${l.status === 'error' ? 'text-red-400' : 'text-green-500'}`}>
                                  {l.status === 'error' ? '✗' : '✓'}
                                </span>
                                <span className="text-gray-500 truncate text-[10px]">{l.subject || l.errorMessage || '—'}</span>
                                <span className="text-gray-400 flex-shrink-0 text-[10px]">
                                  {new Date(l.processedAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-[#1e3a5f] bg-gray-50/50 dark:bg-[#112240]/50">
                    <button onClick={() => setHealthPage(p => Math.max(1, p - 1))} disabled={healthPage === 1}
                      className="text-xs text-gray-500 hover:text-navy-700 disabled:opacity-30 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors">← Prev</button>
                    <span className="text-xs text-gray-400">Page {healthPage} of {totalPages} · {allAccounts.length} accounts</span>
                    <button onClick={() => setHealthPage(p => Math.min(totalPages, p + 1))} disabled={healthPage === totalPages}
                      className="text-xs text-gray-500 hover:text-navy-700 disabled:opacity-30 px-2 py-1 rounded-lg hover:bg-gray-100 transition-colors">Next →</button>
                  </div>
                )}
              </div>
                )
              })()}

              {/* Volume trend chart */}
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Mail Volume — Last 7 Days</p>
                <ResponsiveContainer width="100%" height={120}>
                  <BarChart data={health.volumeTrend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="day" tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 9, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                    <Bar dataKey="processed" fill="#22c55e" name="Processed" radius={[2,2,0,0]} />
                    <Bar dataKey="errors" fill="#ef4444" name="Errors" radius={[2,2,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : (
            <div className="py-6 text-center text-gray-400 text-sm">Failed to load health data</div>
          )}
        </div>
      )}
    </div>

    {/* Mail Accounts */}
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-blue-50 w-8 h-8 rounded-lg flex items-center justify-center">
            <Mail size={15} className="text-blue-500" />
          </div>
          <div>
            <p className="font-display font-semibold text-navy-900">Mail Accounts</p>
            <p className="text-xs text-gray-400">IMAP accounts polled for incoming job emails</p>
          </div>
        </div>
        <button onClick={() => { setShowAdd(!showAdd); setTestResult(null) }}
          className="flex items-center gap-2 bg-navy-800 text-white text-xs px-3 py-2 rounded-lg hover:bg-navy-700 transition-colors">
          <Plus size={13} /> Add Account
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="p-6 border-b border-gray-100 dark:border-[#1e3a5f] bg-gray-50/50 dark:bg-[#112240]/50 space-y-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">New Mail Account</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Client</label>
              <select className="input text-sm" value={form.clientId} onChange={e => set('clientId', e.target.value)}>
                <option value="">Select client...</option>
                {clients.map((c: any) => <option key={c.clientId} value={c.clientId}>{c.companyName}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Email Address</label>
              <input className="input text-sm" placeholder="client1@mycompany.com"
                value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label className="label">IMAP Host</label>
              <input className="input text-sm" placeholder="mail.mycompany.com"
                value={form.imapHost} onChange={e => set('imapHost', e.target.value)} />
            </div>
            <div>
              <label className="label">IMAP Port</label>
              <input className="input text-sm" type="number" placeholder="993"
                value={form.imapPort} onChange={e => set('imapPort', e.target.value)} />
            </div>
            <div>
              <label className="label">Username</label>
              <input className="input text-sm" placeholder="client1@mycompany.com"
                value={form.imapUser} onChange={e => set('imapUser', e.target.value)} />
            </div>
            <div>
              <label className="label">Password</label>
              <input className="input text-sm" type="password" placeholder="••••••••"
                value={form.imapPassword} onChange={e => set('imapPassword', e.target.value)} />
            </div>
            <div>
              <label className="label">Mailbox</label>
              <input className="input text-sm" placeholder="INBOX"
                value={form.mailbox} onChange={e => set('mailbox', e.target.value)} />
            </div>
            <div>
              <label className="label">SMTP Host</label>
              <input className="input text-sm" placeholder="mail.mycompany.com"
                value={form.smtpHost} onChange={e => set('smtpHost', e.target.value)} />
            </div>
            <div>
              <label className="label">SMTP Port</label>
              <input className="input text-sm" type="number" placeholder="465"
                value={form.smtpPort} onChange={e => set('smtpPort', e.target.value)} />
            </div>
            <div>
              <label className="label">Sender Name</label>
              <input className="input text-sm" placeholder="Inkcredible Studio"
                value={form.senderName} onChange={e => set('senderName', e.target.value)} />
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Toggle value={form.useSsl} onChange={v => set('useSsl', v)} />
              <span className="text-sm text-navy-700">Use SSL/TLS</span>
            </div>
          </div>
          {testResult && (
            <div className={`text-xs px-3 py-2 rounded-lg font-medium ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
              {testResult.success ? '✓ ' : '✗ '}{testResult.message}
            </div>
          )}
          <div className="flex items-center gap-3">
            <button onClick={handleTest} disabled={testing || !form.imapHost || !form.imapUser || !form.imapPassword}
              className="text-xs border border-gray-200 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2">
              <Wifi size={12} /> {testing ? 'Testing...' : 'Test Connection'}
            </button>
            <button onClick={handleSave} disabled={saving || !form.clientId || !form.email || !form.imapHost || !form.imapUser || !form.imapPassword}
              className="text-xs bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50">
              {saving ? 'Saving...' : 'Save Account'}
            </button>
            <button onClick={() => { setShowAdd(false); setTestResult(null) }}
              className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {/* Accounts list */}
      {loading ? (
        <div className="py-8 text-center text-gray-400 text-sm">Loading...</div>
      ) : accounts.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">No mail accounts configured</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {accounts.map((a: any) => (
            <div key={a.id}>
              <div className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${a.isActive ? 'bg-green-400' : 'bg-gray-300'}`} />
                  <div>
                    <p className="text-sm font-medium text-navy-800">{a.email}</p>
                    <p className="text-xs text-gray-400">{a.clientName} · {a.imapHost}:{a.imapPort}</p>
                    {a.lastPolledAt && (
                      <p className="text-xs text-gray-400">Last polled: {new Date(a.lastPolledAt).toLocaleString('en-IN')}</p>
                    )}
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-[10px] text-gray-400">Poll every:</span>
                      <select className="text-[10px] border border-gray-200 rounded px-1 py-0.5 text-gray-600 bg-white dark:bg-[#1c3a5e] dark:border-[#1e3a5f]"
                        value={a.pollIntervalMinutes || 5}
                        onChange={e => handleUpdateInterval(a.id, parseInt(e.target.value))}>
                        <option value={2}>2 min</option>
                        <option value={3}>3 min</option>
                        <option value={5}>5 min</option>
                        <option value={10}>10 min</option>
                        <option value={15}>15 min</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => {
  if (showLogs === a.id) { setShowLogs(null); return }
  handleViewLogs(a.id)
}}
                    className="text-xs text-blue-500 hover:text-blue-700 font-medium">
                    {showLogs === a.id ? 'Hide logs' : 'View logs'}
                  </button>
                  <button onClick={() => handleToggle(a.id, a.isActive)}
                    className={`text-xs font-medium px-2 py-1 rounded-lg ${a.isActive ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                    {a.isActive ? <Wifi size={12} /> : <WifiOff size={12} />}
                  </button>
                  <button onClick={() => {
                    setEditAccount(a)
                    setEditForm({ imapHost: a.imapHost, imapPort: a.imapPort.toString(), imapUser: a.imapUser, imapPassword: '', useSsl: a.useSsl, mailbox: a.mailbox, smtpHost: a.smtpHost || '', smtpPort: (a.smtpPort || 465).toString(), senderName: a.senderName || '', isActive: a.isActive })
                  }} className="text-blue-400 hover:text-blue-600 p-1">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => handleDelete(a.id)}
                    className="text-red-400 hover:text-red-600 p-1">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              {/* Logs panel */}
              {showLogs === a.id && (
                <div className="px-6 pb-4 bg-gray-50/50 dark:bg-[#112240]/50">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Recent Activity</p>
                  {logs.length === 0 ? (
                    <p className="text-xs text-gray-400">No logs yet</p>
                  ) : (
                    <div className="space-y-1.5">
                      {logs.map((l: any) => (
                        <div key={l.id} className={`flex items-start gap-3 text-xs px-3 py-2 rounded-lg ${l.status === 'error' ? 'bg-red-50' : 'bg-gray-50 dark:bg-[#1a3050] border border-gray-100'}`}>
                          <span className={`font-semibold flex-shrink-0 ${l.status === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                            {l.status === 'error' ? '✗' : '✓'}
                          </span>
                          <div className="flex-1">
                            <p className="text-navy-700">{l.subject}</p>
                            {l.jobCode && <p className="text-gray-400">{l.jobCode} — {l.jobTitle}</p>}
                            {l.errorMessage && <p className="text-red-500">{l.errorMessage}</p>}
                          </div>
                          <span className="text-gray-400 flex-shrink-0">
                            {new Date(l.processedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
      {/* Edit Modal */}
      {editAccount && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-navy-900/50 backdrop-blur-sm" onClick={() => setEditAccount(null)} />
          <div className="relative bg-white dark:bg-[#1c3a5e] rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-navy-900">Edit Mail Account</h2>
              <p className="text-xs font-mono text-gray-400">{editAccount.email}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">IMAP Host</label>
                <input className="input text-sm" value={editForm.imapHost} onChange={e => setEditForm((f: any) => ({ ...f, imapHost: e.target.value }))} />
              </div>
              <div>
                <label className="label">IMAP Port</label>
                <input className="input text-sm" type="number" value={editForm.imapPort} onChange={e => setEditForm((f: any) => ({ ...f, imapPort: e.target.value }))} />
              </div>
              <div>
                <label className="label">Username</label>
                <input className="input text-sm" value={editForm.imapUser} onChange={e => setEditForm((f: any) => ({ ...f, imapUser: e.target.value }))} />
              </div>
              <div>
                <label className="label">Password</label>
                <input className="input text-sm" type="password" placeholder="Leave blank to keep current"
                  value={editForm.imapPassword} onChange={e => setEditForm((f: any) => ({ ...f, imapPassword: e.target.value }))} />
              </div>
              <div>
                <label className="label">SMTP Host</label>
                <input className="input text-sm" value={editForm.smtpHost} onChange={e => setEditForm((f: any) => ({ ...f, smtpHost: e.target.value }))} />
              </div>
              <div>
                <label className="label">SMTP Port</label>
                <input className="input text-sm" type="number" value={editForm.smtpPort} onChange={e => setEditForm((f: any) => ({ ...f, smtpPort: e.target.value }))} />
              </div>
              <div>
                <label className="label">Sender Name</label>
                <input className="input text-sm" value={editForm.senderName} onChange={e => setEditForm((f: any) => ({ ...f, senderName: e.target.value }))} />
              </div>
              <div>
                <label className="label">Mailbox</label>
                <input className="input text-sm" value={editForm.mailbox} onChange={e => setEditForm((f: any) => ({ ...f, mailbox: e.target.value }))} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Toggle value={editForm.useSsl} onChange={v => setEditForm((f: any) => ({ ...f, useSsl: v }))} />
              <span className="text-sm text-navy-700">Use SSL/TLS</span>
            </div>
            <div className="flex gap-3">
              <button onClick={async () => {
                setEditSaving(true)
                try {
                  const payload: any = { imapHost: editForm.imapHost, imapPort: parseInt(editForm.imapPort), imapUser: editForm.imapUser, useSsl: editForm.useSsl, mailbox: editForm.mailbox, smtpHost: editForm.smtpHost, smtpPort: parseInt(editForm.smtpPort), senderName: editForm.senderName }
                  if (editForm.imapPassword) payload.imapPassword = editForm.imapPassword
                  await api.patch(`/mail/${editAccount.id}`, payload)
                  setEditAccount(null)
                  fetchAccounts()
                } catch { alert('Failed to save') }
                setEditSaving(false)
              }} disabled={editSaving}
                className="flex-1 bg-orange-500 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-orange-600 disabled:opacity-50">
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
              <button onClick={() => setEditAccount(null)} className="flex-1 btn-ghost">Cancel</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

const ALL_PERMISSIONS = [
  { key: 'view_dashboard', label: 'View Dashboard Stats' },
  // ── Jobs ──
  { key: 'access_jobs', label: 'Access Jobs Page' },
  { key: 'allocate_jobs', label: 'Allocate Jobs' },
  { key: 'self_allocate_jobs', label: 'Self Allocate Jobs' },
  { key: 'approve_output', label: 'Approve Output' },
  { key: 'return_to_artist', label: 'Return Job to Artist' },
  { key: 'send_client_query', label: 'Send Client Query' },
  { key: 'edit_display_title', label: 'Edit Job Title' },
  { key: 'edit_job_type', label: 'Edit Job Type' },
  { key: 'manage_job_types', label: 'Manage Job Types (Complex/Flag)' },
  { key: 'clear_job_flags', label: 'Clear Job Flags' },
  { key: 'delete_log_entries', label: 'Delete Log Entries' },
  { key: 'club_jobs', label: 'Merge / Delete Jobs' },
  { key: 'manage_dispatch', label: 'Manage Dispatch' },
  { key: 'edit_timer_minutes', label: 'Edit Timer Minutes' },
  { key: 'post_notices', label: 'Post on Ink Bulletin' },
  { key: 'view_team_energy', label: 'View Team Energy Indicator' },
  { key: 'enter_expenses', label: 'Enter Expenses (own entries only)' },
  { key: 'manage_expenses', label: 'Manage Expenses (all entries)' },
  { key: 'manage_invoices', label: 'Manage Invoices' },
  { key: 'view_expense_analytics', label: 'View Expense Analytics' },
  { key: 'view_invoice_analytics', label: 'View Invoice Analytics' },
  { key: 'post_star_award', label: 'Post Monthly Star Award' },
  // ── Clients ──
  { key: 'manage_clients', label: 'Manage Clients' },
  { key: 'view_client_details', label: 'View Client Details' },
  { key: 'edit_client_guideline', label: 'Edit Client Guideline' },
  { key: 'manage_subscriptions', label: 'Manage Subscriptions' },
  { key: 'view_billing', label: 'View Billing' },
  // ── Staff ──
  { key: 'manage_staff', label: 'Manage Staff' },
  { key: 'manage_shift_schedule', label: 'Manage Shift Schedule' },
  // ── Reports ──
  { key: 'view_job_reports', label: 'View Job Reports' },
  { key: 'view_client_reports', label: 'View Client Reports' },
  { key: 'view_performance_all', label: 'View All Performance' },
  { key: 'export_reports', label: 'Export Reports' },
  { key: 'smart_search', label: 'Smart Search (Ctrl+K)' },
  { key: 'view_reports_dashboard', label: 'View Reports Dashboard' },
  // ── Leave & Attendance ──
  { key: 'apply_leave', label: 'Apply for Leave' },
  { key: 'approve_leave', label: 'Approve Leave' },
  { key: 'mark_attendance', label: 'Mark Attendance' },
  { key: 'view_attendance_all', label: 'View All Attendance' },
  // ── Settings ──
  { key: 'manage_system_settings', label: 'Manage System Settings' },
  { key: 'manage_roles', label: 'Manage Roles' },
  { key: 'manage_mail_accounts', label: 'Manage Mail Accounts' },
  { key: 'manage_notification_settings', label: 'Manage Notification Settings' },
  { key: 'manage_chat_groups', label: 'Create Chat Groups (InkConnect)' },
  { key: 'post_announcements', label: 'Post Announcements' },
  { key: 'receive_birthday_notifications', label: 'Receive Birthday & Anniversary Notifications' },
  { key: 'view_analytics_dashboard', label: 'View Analytics Dashboard' },
  { key: 'manage_onboarding', label: 'Manage Staff Onboarding' },
  // ── Brandster Pins ──
  { key: 'add_brandster_request', label: 'Add Brandster Pins Request (Grouped Orders)' },
  { key: 'handle_client_support', label: 'Handle Client Support Chat' },
  { key: 'can_work_on_jobs', label: 'Can Work on Jobs (shown in artist lists)' },
  { key: 'exclude_from_leaderboard', label: 'Exclude from Leaderboard' },
  { key: 'view_staff_performance_card', label: 'View Staff Performance Card' },
  { key: 'leaderboard_enabled', label: 'Leaderboard on Performance Page' },
  { key: 'dashboard_leaderboard_enabled', label: 'Leaderboard on Dashboard' },
  { key: 'view_client_flow_chart', label: 'View Client Jobs Flow Chart' },
  { key: 'manage_inkdeals', label: 'Manage InkDeals (Plans & Offers)' },
  { key: 'manage_trial_plans', label: 'Manage Free Trial Plans' },
  { key: 'access_danger_zone', label: 'Access Danger Zone' },
  { key: 'manage_points', label: 'Manage Performance Points' },
]

function RoleManagementSection() {
  const [roles, setRoles] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedRole, setSelectedRole] = useState<any>(null)
  const [editPerms, setEditPerms] = useState<Record<string, boolean>>({})
  const [editLabel, setEditLabel] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [newRole, setNewRole] = useState({ roleName: '', label: '', description: '' })
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  const fetchRoles = async () => {
    setLoading(true)
    try {
      const res = await api.get('/settings/roles')
      setRoles(res.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchRoles() }, [])

  const openRole = (role: any) => {
    setSelectedRole(role)
    setEditLabel(role.label)
    setEditPerms({ ...role.permissions })
    setSaved(false)
  }

  const handleSave = async () => {
    if (!selectedRole) return
    setSaving(true)
    try {
      await api.patch(`/settings/roles/${selectedRole.roleName}`, { label: editLabel })
      await api.patch(`/settings/roles/${selectedRole.roleName}/permissions`, { permissions: editPerms })
      setSaved(true)
      toast.success('Permissions saved. Affected users must re-login to see changes.')
      setTimeout(() => setSaved(false), 2000)
      fetchRoles()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  const handleCreate = async () => {
    if (!newRole.roleName || !newRole.label) { setError('Role name and label are required'); return }
    setCreating(true)
    setError('')
    try {
      await api.post('/settings/roles', { ...newRole, isStaffRole: true })
      setShowCreate(false)
      setNewRole({ roleName: '', label: '', description: '' })
      fetchRoles()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to create role')
    }
    setCreating(false)
  }

  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-navy-900 text-lg">Role Management</h2>
          <p className="text-xs text-gray-400 mt-0.5">Create roles and configure their permissions</p>
        </div>
        <button
          onClick={() => { setShowCreate(true); setError('') }}
          className="flex items-center gap-2 text-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl transition-colors"
        >
          <Plus size={14} /> New Role
        </button>
      </div>

      {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      {showCreate && (
        <div className="border border-orange-200 dark:border-orange-500/20 rounded-xl p-4 bg-orange-50 dark:bg-[#1a2a40] space-y-3">
          <p className="text-sm font-semibold text-orange-700">Create New Role</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Role Key (no spaces)</label>
              <input
                className="input text-sm"
                placeholder="e.g. supervisor"
                value={newRole.roleName}
                onChange={e => setNewRole(p => ({ ...p, roleName: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Display Label</label>
              <input
                className="input text-sm"
                placeholder="e.g. Supervisor"
                value={newRole.label}
                onChange={e => setNewRole(p => ({ ...p, label: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Description (optional)</label>
            <input
              className="input text-sm"
              placeholder="Brief description of this role"
              value={newRole.description}
              onChange={e => setNewRole(p => ({ ...p, description: e.target.value }))}
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={creating}
              className="text-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl disabled:opacity-50 transition-colors">
              {creating ? 'Creating...' : 'Create Role'}
            </button>
            <button onClick={() => setShowCreate(false)}
              className="text-sm border border-gray-200 text-gray-500 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400">Loading roles...</p>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {roles.filter(r => r.roleName !== 'admin').map(role => (
            <div key={role.roleName} className={`border rounded-xl overflow-hidden transition-all ${selectedRole?.roleName === role.roleName ? 'border-orange-300' : 'border-gray-100'}`}>
              <div
                className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a3050] transition-colors"
                onClick={() => selectedRole?.roleName === role.roleName ? setSelectedRole(null) : openRole(role)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${role.isActive ? 'bg-green-400' : 'bg-gray-300'}`} />
                  <div>
                    <p className="text-sm font-semibold text-navy-900">{role.label}</p>
                    <p className="text-xs text-gray-400">{role.roleName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">
                    {Object.values(role.permissions).filter(Boolean).length} / {ALL_PERMISSIONS.length} permissions
                  </span>
                  <span className="text-gray-300 text-sm">{selectedRole?.roleName === role.roleName ? '▲' : '▼'}</span>
                </div>
              </div>

              {selectedRole?.roleName === role.roleName && (
                <div className="border-t border-gray-100 dark:border-[#1e3a5f] p-4 space-y-4 bg-white dark:bg-[#16304f]">
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Display Label</label>
                    <input
                      className="input text-sm max-w-xs"
                      value={editLabel}
                      onChange={e => setEditLabel(e.target.value)}
                    />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Permissions</p>
                    <div className="grid grid-cols-1 gap-y-3">
                      {(() => {
                        const sections: { title: string, keys: string[] }[] = [
                          { title: 'Dashboard', keys: ['view_dashboard'] },
                          { title: 'Jobs', keys: ['access_jobs', 'allocate_jobs', 'self_allocate_jobs', 'approve_output', 'return_to_artist', 'send_client_query', 'edit_display_title', 'edit_job_type', 'manage_job_types', 'clear_job_flags', 'delete_log_entries', 'club_jobs', 'manage_dispatch', 'edit_timer_minutes'] },
                          { title: 'Clients', keys: ['manage_clients', 'view_client_details', 'manage_subscriptions', 'view_billing', 'edit_client_guideline'] },
                          { title: 'Staff', keys: ['manage_staff', 'manage_shift_schedule'] },
                          { title: 'Reports', keys: ['view_job_reports', 'view_client_reports', 'view_performance_all', 'export_reports', 'view_reports_dashboard', 'smart_search'] },
                          { title: 'Leave & Attendance', keys: ['apply_leave', 'approve_leave', 'mark_attendance', 'view_attendance_all'] },
                          { title: 'Settings', keys: ['manage_system_settings', 'manage_roles', 'manage_mail_accounts', 'manage_notification_settings', 'manage_chat_groups', 'post_announcements', 'manage_inkdeals', 'manage_trial_plans', 'access_danger_zone', 'manage_points'] },
                          { title: 'HR & Notifications', keys: ['receive_birthday_notifications', 'manage_onboarding', 'handle_client_support'] },
                          { title: 'Notice Board & Awards', keys: ['post_notices', 'post_star_award', 'view_team_energy'] },
                          { title: 'Finance', keys: ['enter_expenses', 'manage_expenses', 'manage_invoices', 'view_expense_analytics', 'view_invoice_analytics'] },
                          { title: 'Analytics', keys: ['view_analytics_dashboard', 'view_client_flow_chart'] },
                          { title: 'Performance & Leaderboard', keys: ['exclude_from_leaderboard', 'can_work_on_jobs', 'view_staff_performance_card'] },
                          { title: 'Brandster Pins', keys: ['add_brandster_request'] },
                        ]
                        return sections.map(section => (
                          <div key={section.title} className="col-span-2">
                            <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-2 mt-2 border-b border-orange-100 dark:border-orange-900/30 pb-1">{section.title}</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {ALL_PERMISSIONS.filter(p => section.keys.includes(p.key)).map(p => (
                                <label key={p.key} className="flex items-center gap-2 cursor-pointer group">
                                  <input
                                    type="checkbox"
                                    checked={editPerms[p.key] || false}
                                    onChange={e => setEditPerms(prev => ({ ...prev, [p.key]: e.target.checked }))}
                                    className="w-4 h-4 accent-orange-500"
                                  />
                                  <span className="text-sm text-gray-600 dark:text-gray-300 group-hover:text-navy-900 dark:group-hover:text-white transition-colors">{p.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        ))
                      })()}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                    <button onClick={handleSave} disabled={saving}
                      className="text-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl disabled:opacity-50 transition-colors">
                      {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Changes'}
                    </button>
                    <button onClick={() => setSelectedRole(null)}
                      className="text-sm border border-gray-200 text-gray-500 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">
                      Cancel
                    </button>
                    {!selectedRole.isProtected && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Delete role "${selectedRole.label}"? This cannot be undone.`)) return
                          try {
                            await api.delete(`/settings/roles/${selectedRole.roleName}`)
                            toast.success('Role deleted')
                            fetchRoles()
                            setSelectedRole(null)
                          } catch (e: any) {
                            setError(e.response?.data?.error || 'Failed to delete role')
                          }
                        }}
                        className="text-sm border border-red-200 text-red-400 px-4 py-2 rounded-xl hover:bg-red-50 transition-colors ml-auto">
                        Delete Role
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function MailTemplatesSection() {
  const [templates, setTemplates] = useState<any[]>([])
  const [clients, setClients] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState({ clientId: '', templateType: 'completion', subjectPrefix: '', bodyTemplate: '' })
  const [tmplPage, setTmplPage] = useState(1)
  const TMPL_LIMIT = 5
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const defaultBody = `<p>Hi,</p>
<p>Your job <strong>{{jobCode}} — {{jobTitle}}</strong> has been completed.</p>
<p>{{clientNote}}</p>
<p>{{attachmentNote}}</p>
<br/>
<p>Best regards,<br/>{{senderName}}</p>`

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [tmplRes, clientRes] = await Promise.all([
        api.get('/mail/templates'),
        api.get('/clients/basic')
      ])
      setTemplates(tmplRes.data)
      setClients(clientRes.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchAll() }, [])

  const openNew = () => {
    setEditingId(null)
    setForm({ clientId: '', templateType: 'completion', subjectPrefix: '', bodyTemplate: defaultBody })
    setShowForm(true)
    setError('')
  }

  const openEdit = (t: any) => {
    setEditingId(t.id)
    setForm({ clientId: t.clientId || '', templateType: t.templateType, subjectPrefix: t.subjectPrefix || '', bodyTemplate: t.bodyTemplate })
    setShowForm(true)
    setError('')
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')
    try {
      await api.post('/mail/templates', form)
      setShowForm(false)
      setTmplPage(1)
      fetchAll()
      toast.success('Template saved')
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to save')
    }
    setSaving(false)
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this template?')) return
    try {
      await api.delete(`/mail/templates/${id}`)
      fetchAll()
    } catch {}
  }

  const TEMPLATE_TYPES = [
    { value: 'completion', label: 'Job Completion' },
    { value: 'subscription_expiry_alert', label: 'Subscription Expiry Alert (3 days)' },
    { value: 'subscription_expired', label: 'Subscription Expired' },
    { value: 'jobs_exhausted', label: 'Job Limit Reached' },
    { value: 'revision_exhausted', label: 'Revision Limit Reached' },
    { value: 'revision_and_jobs_exhausted', label: 'Revisions & Jobs Both Exhausted' },
    { value: 'query', label: 'Client Query' },
  ]

  return (
    <div className="card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-navy-900 text-lg">Mail Templates</h2>
          <p className="text-xs text-gray-400 mt-0.5">Customize email content sent to clients. Use variables: <code className="bg-gray-100 px-1 rounded">{'{{jobCode}}'}</code> <code className="bg-gray-100 px-1 rounded">{'{{jobTitle}}'}</code> <code className="bg-gray-100 px-1 rounded">{'{{clientNote}}'}</code> <code className="bg-gray-100 px-1 rounded">{'{{attachmentNote}}'}</code> <code className="bg-gray-100 px-1 rounded">{'{{senderName}}'}</code></p>
        </div>
        <button onClick={openNew} className="flex items-center gap-2 text-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl transition-colors">
          <Plus size={14} /> New Template
        </button>
      </div>

      {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

      {showForm && (
        <div className="border border-orange-200 dark:border-orange-500/20 rounded-xl p-4 bg-orange-50 dark:bg-[#1a2a40] space-y-3">
          <p className="text-sm font-semibold text-orange-700">{editingId ? 'Edit Template' : 'New Template'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {!['subscription_expired', 'jobs_exhausted', 'subscription_expiry_alert', 'revision_exhausted', 'revision_and_jobs_exhausted'].includes(form.templateType) && (
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Client (leave blank for default)</label>
              <select className="input text-sm" value={form.clientId} onChange={e => setForm(p => ({ ...p, clientId: e.target.value }))}>
                <option value="">Default (all clients)</option>
                {clients.map((c: any) => <option key={c.clientId} value={c.clientId}>{c.companyName || c.abbreviation}</option>)}
              </select>
            </div>
            )}
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Template Type</label>
              <select className="input text-sm" value={form.templateType} onChange={e => setForm(p => ({ ...p, templateType: e.target.value, clientId: ['subscription_expired', 'jobs_exhausted', 'subscription_expiry_alert', 'revision_exhausted', 'revision_and_jobs_exhausted'].includes(e.target.value) ? '' : p.clientId }))}>
                {TEMPLATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Subject Prefix (optional)</label>
            <input className="input text-sm" placeholder="e.g. [Completed]" value={form.subjectPrefix} onChange={e => setForm(p => ({ ...p, subjectPrefix: e.target.value }))} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Email Body (HTML supported)</label>
            <textarea rows={8} className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono text-gray-700 resize-y focus:outline-none focus:border-orange-400" value={form.bodyTemplate} onChange={e => setForm(p => ({ ...p, bodyTemplate: e.target.value }))} />
            <div className="mt-2 p-3 bg-gray-50 dark:bg-[#112240] rounded-xl">
              <p className="text-xs font-semibold text-gray-500 mb-1">Available Variables</p>
              {form.templateType === 'completion' && (
                <p className="text-xs font-mono text-gray-400">{'{{clientName}} {{jobCode}} {{displayTitle}} {{requestType}} {{portalUrl}} {{senderName}}'}</p>
              )}
              {form.templateType === 'welcome' && (
                <p className="text-xs font-mono text-gray-400">{'{{clientName}} {{clientId}} {{username}} {{password}} {{portalUrl}} {{senderName}}'}</p>
              )}
              {(['subscription_expired', 'jobs_exhausted', 'subscription_expiry_alert'].includes(form.templateType)) && (
                <p className="text-xs font-mono text-gray-400">{'{{clientName}} {{planName}} {{endDate}} {{jobsUsed}} {{totalJobsAllowed}} {{daysLeft}} {{portalUrl}}'}</p>
              )}
              {form.templateType === 'revision_exhausted' && (
                <p className="text-xs font-mono text-gray-400">{'{{clientName}} {{planName}} {{freeRevisions}} {{revisionsUsed}} {{jobsRemaining}} {{portalUrl}}'}</p>
              )}
              {form.templateType === 'revision_and_jobs_exhausted' && (
                <p className="text-xs font-mono text-gray-400">{'{{clientName}} {{planName}} {{freeRevisions}} {{revisionsUsed}} {{portalUrl}}'}</p>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving} className="text-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-xl disabled:opacity-50 transition-colors">
              {saving ? 'Saving...' : 'Save Template'}
            </button>
            <button onClick={() => setShowForm(false)} className="text-sm border border-gray-200 text-gray-500 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <p className="text-sm text-gray-400">Loading...</p> : templates.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">No templates yet. Default system template will be used.</p>
      ) : (
        <div className="space-y-2">
          {templates.slice((tmplPage - 1) * TMPL_LIMIT, tmplPage * TMPL_LIMIT).map(t => (
            <div key={t.id} className="border border-gray-100 rounded-xl px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors">
              <div>
                <p className="text-sm font-semibold text-navy-900">{t.clientName} — {TEMPLATE_TYPES.find(x => x.value === t.templateType)?.label || t.templateType}</p>
                {t.subjectPrefix && <p className="text-xs text-gray-400">Subject prefix: {t.subjectPrefix}</p>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(t)} className="text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors">Edit</button>
                <button onClick={() => handleDelete(t.id)} className="text-xs px-3 py-1.5 border border-red-100 rounded-lg text-red-400 hover:bg-red-50 transition-colors">Delete</button>
              </div>
            </div>
          ))}
          {templates.length > TMPL_LIMIT && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-gray-400">{templates.length} templates — page {tmplPage} of {Math.ceil(templates.length / TMPL_LIMIT)}</p>
              <div className="flex gap-2">
                <button disabled={tmplPage === 1} onClick={() => setTmplPage(p => p - 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Previous</button>
                <button disabled={tmplPage * TMPL_LIMIT >= templates.length} onClick={() => setTmplPage(p => p + 1)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50">Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function NotificationPreferencesSection() {
  const [prefs, setPrefs] = useState<any>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api.get('/settings/notification-preferences').then(res => setPrefs(res.data)).catch(() => {})
  }, [])

  const toggle = async (key: string) => {
    const updated = { ...prefs, [key]: !prefs[key] }
    setPrefs(updated)
    try {
      await api.patch('/settings/notification-preferences', updated)
      setSaved(true)
      setTimeout(() => setSaved(false), 1500)
    } catch (e: any) {
      console.error('Failed to save notification preference:', e.response?.data || e.message)
      toast.error('Failed to save preference')
      setPrefs(prefs) // revert on error
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.patch('/settings/notification-preferences', prefs)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {}
    setSaving(false)
  }

  const NOTIF_ITEMS = [
    { key: 'jobCreated', label: 'New Job Created', description: 'When a new job is submitted by email or portal' },
    { key: 'jobAllocated', label: 'Job Allocated', description: 'When a job is allocated to an artist' },
    { key: 'jobRevision', label: 'Job Revision', description: 'When a job is sent back for revision' },
    { key: 'leaveApplied', label: 'Leave Applied', description: 'When a staff member applies for leave' },
    { key: 'leaveApproved', label: 'Leave Approved/Rejected', description: 'When a leave application is reviewed' },
    { key: 'newClientSubscribed', label: 'New Client Subscribed', description: 'When a new client subscribes via PayPal checkout' },
    { key: 'clientRenewed', label: 'Client Renewed', description: 'When an existing client renews their subscription via PayPal' },
  ]

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-navy-900 text-lg">Notification Preferences</h2>
          <p className="text-xs text-gray-400 mt-0.5">Choose which notifications you want to receive</p>
        </div>
        {saved && <span className="text-xs text-green-500 font-medium">✓ Saved</span>}
      </div>
      {!prefs ? (
        <div className="px-6 py-8 text-center text-gray-400 text-sm">Loading...</div>
      ) : (
        <div className="divide-y divide-gray-50">
          {NOTIF_ITEMS.map(({ key, label, description }) => (
            <div key={key} className="px-6 py-4 flex items-center justify-between gap-6">
              <div>
                <p className="text-sm font-medium text-navy-800">{label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{description}</p>
              </div>
              <button onClick={() => toggle(key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${prefs[key] ? 'bg-orange-500' : 'bg-gray-200'}`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${prefs[key] ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}


function PasswordField({ label, field, form, setForm, show, setShow }: {
  label: string,
  field: 'currentPassword' | 'newPassword' | 'confirmPassword',
  form: { currentPassword: string, newPassword: string, confirmPassword: string },
  setForm: React.Dispatch<React.SetStateAction<{ currentPassword: string, newPassword: string, confirmPassword: string }>>,
  show: boolean,
  setShow: (v: boolean) => void
}) {
  const EyeIcon = ({ show }: { show: boolean }) => show
    ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
    : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>

  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          className="input text-sm pr-10"
          placeholder="••••••••"
          value={form[field]}
          onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
        />
        <button type="button" onClick={() => setShow(!show)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          <EyeIcon show={show} />
        </button>
      </div>
    </div>
  )
}

function ChangePasswordSection() {
  const { user } = useAuth()
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  if (user?.roleName !== 'admin') return null

  const handleSubmit = async () => {
    setError('')
    setSuccess(false)
    if (!form.currentPassword || !form.newPassword || !form.confirmPassword) {
      setError('All fields are required'); return
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match'); return
    }
    if (form.newPassword.length < 8) {
      setError('New password must be at least 8 characters'); return
    }
    setSaving(true)
    try {
      await api.post('/settings/change-password', form)
      setSuccess(true)
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setTimeout(() => setSuccess(false), 3000)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to change password')
    }
    setSaving(false)
  }

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center gap-3">
        <div className="w-8 h-8 bg-navy-50 dark:bg-navy-800 rounded-lg flex items-center justify-center">
          <Lock size={15} className="text-navy-600 dark:text-navy-300" />
        </div>
        <div>
          <h2 className="font-display font-semibold text-navy-900 dark:text-white">Change Password</h2>
          <p className="text-xs text-gray-400 mt-0.5">Update your admin account password</p>
        </div>
      </div>
      <div className="p-6 space-y-4 max-w-sm">
        <PasswordField label="Current Password" field="currentPassword" form={form} setForm={setForm} show={showCurrent} setShow={setShowCurrent} />
        <PasswordField label="New Password" field="newPassword" form={form} setForm={setForm} show={showNew} setShow={setShowNew} />
        <PasswordField label="Confirm New Password" field="confirmPassword" form={form} setForm={setForm} show={showConfirm} setShow={setShowConfirm} />
        {error && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{error}</p>}
        {success && <p className="text-xs text-green-600 bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded-lg">✓ Password changed successfully</p>}
        <button onClick={handleSubmit} disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors">
          {saving ? 'Saving...' : 'Change Password'}
        </button>
      </div>
    </div>
  )
}

function GeminiStatusSection() {
  const [status, setStatus] = useState<'idle' | 'testing' | 'ok' | 'error'>('idle')
  const [responseTime, setResponseTime] = useState<number | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [lastChecked, setLastChecked] = useState<Date | null>(null)
  const [failCount, setFailCount] = useState<number | null>(null)

  useEffect(() => {
    api.get('/reports/gemini-status').then(res => {
      setFailCount(res.data.failCount24h ?? null)
      if (res.data.lastError) setErrorMsg(res.data.lastError)
    }).catch(() => {})
  }, [])

  const handleTest = async () => {
    setStatus('testing')
    setErrorMsg('')
    setResponseTime(null)
    const start = Date.now()
    try {
      await api.post('/reports/gemini-test')
      setResponseTime(Date.now() - start)
      setStatus('ok')
      setLastChecked(new Date())
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.error || 'Gemini did not respond')
      setStatus('error')
      setLastChecked(new Date())
    }
  }

  return (
    <div className="space-y-4">
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-blue-50 dark:bg-blue-900/20 w-8 h-8 rounded-lg flex items-center justify-center">
              <Zap size={15} className="text-blue-500" />
            </div>
            <div>
              <p className="font-display font-semibold text-navy-900 dark:text-white">Gemini AI Service</p>
              <p className="text-xs text-gray-400">Test connectivity and monitor AI service health</p>
            </div>
          </div>
          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${status === 'ok' ? 'bg-green-400' : status === 'error' ? 'bg-red-400' : status === 'testing' ? 'bg-yellow-400 animate-pulse' : 'bg-gray-300'}`} />
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-[#112240] rounded-xl px-4 py-3">
              <p className="text-xs text-gray-400 mb-1">Primary Model</p>
              <p className="text-sm font-mono font-medium text-navy-800 dark:text-white">gemini-2.5-flash</p>
            </div>
            <div className="bg-gray-50 dark:bg-[#112240] rounded-xl px-4 py-3">
              <p className="text-xs text-gray-400 mb-1">Fallback Model</p>
              <p className="text-sm font-mono font-medium text-navy-800 dark:text-white">gemini-2.0-flash-lite</p>
            </div>
            <div className="bg-gray-50 dark:bg-[#112240] rounded-xl px-4 py-3">
              <p className="text-xs text-gray-400 mb-1">AI Filter Failures (24h)</p>
              <p className={`text-sm font-bold ${failCount === null ? 'text-gray-400' : failCount > 5 ? 'text-red-500' : failCount > 0 ? 'text-amber-500' : 'text-green-500'}`}>
                {failCount === null ? '—' : failCount}
              </p>
            </div>
          </div>

          {status === 'ok' && (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 dark:bg-green-900/10 rounded-xl px-4 py-3">
              <CheckCircle size={15} />
              <span>Gemini responded in {responseTime}ms</span>
              {lastChecked && <span className="text-xs text-gray-400 ml-auto">{lastChecked.toLocaleTimeString()}</span>}
            </div>
          )}
          {status === 'error' && (
            <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 dark:bg-red-900/10 rounded-xl px-4 py-3">
              <XCircle size={15} />
              <span className="flex-1">{errorMsg || 'Connection failed'}</span>
              {lastChecked && <span className="text-xs text-gray-400 ml-auto">{lastChecked.toLocaleTimeString()}</span>}
            </div>
          )}

          <button onClick={handleTest} disabled={status === 'testing'}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors">
            <Zap size={14} />
            {status === 'testing' ? 'Testing...' : 'Test Now'}
          </button>
        </div>
      </div>
    </div>
  )
}

function DangerZoneSection() {
  const [showClearJobs, setShowClearJobs] = useState(false)
  const [showClearStaff, setShowClearStaff] = useState(false)
  const [showClearClients, setShowClearClients] = useState(false)
  const [showInactiveClients, setShowInactiveClients] = useState(false)
  const [inactiveClients, setInactiveClients] = useState<any[]>([])
  const [passwordInputs, setPasswordInputs] = useState<Record<string, string>>({})
  const [clearing, setClearing] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')

  const fetchInactiveClients = async () => {
    try {
      const res = await api.get('/settings/danger/inactive-clients')
      setInactiveClients(res.data)
    } catch {}
  }

  const handleClearJobs = async () => {
    if (!confirmPassword) { setError('Password required'); return }
    setClearing(true); setError('')
    try {
      await api.post('/settings/danger/clear-jobs', { password: confirmPassword })
      setShowClearJobs(false); setConfirmPassword('')
      toast.success('All jobs cleared')
    } catch (e: any) { setError(e.response?.data?.error || 'Failed') }
    setClearing(false)
  }

  const handleClearStaff = async () => {
    if (!confirmPassword) { setError('Password required'); return }
    setClearing(true); setError('')
    try {
      await api.post('/settings/danger/clear-staff', { password: confirmPassword })
      setShowClearStaff(false); setConfirmPassword('')
      toast.success('All staff cleared')
    } catch (e: any) { setError(e.response?.data?.error || 'Failed') }
    setClearing(false)
  }

  const handleClearClients = async () => {
    if (!confirmPassword) { setError('Password required'); return }
    setClearing(true); setError('')
    try {
      await api.post('/settings/danger/clear-clients', { password: confirmPassword })
      setShowClearClients(false); setConfirmPassword('')
      toast.success('All clients cleared')
    } catch (e: any) { setError(e.response?.data?.error || 'Failed') }
    setClearing(false)
  }

  const handleDeleteInactiveClient = async (userId: string) => {
    const pw = passwordInputs[userId]
    if (!pw) { setError('Password required for ' + userId); return }
    try {
      await api.delete(`/settings/danger/inactive-clients/${userId}`, { data: { password: pw } })
      toast.success('Client deleted')
      fetchInactiveClients()
    } catch (e: any) { setError(e.response?.data?.error || 'Failed') }
  }

  return (
    <div className="card overflow-hidden border-red-100 dark:border-red-900/30">
      <div className="px-6 py-4 border-b border-red-100 dark:border-red-900/30 flex items-center gap-3 bg-red-50/50 dark:bg-red-900/10">
        <div className="w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
          <AlertTriangle size={15} className="text-red-500" />
        </div>
        <div>
          <h2 className="font-display font-semibold text-red-700 dark:text-red-400">Danger Zone</h2>
          <p className="text-xs text-red-400 mt-0.5">Irreversible actions — proceed with extreme caution</p>
        </div>
      </div>
      <div className="divide-y divide-red-50 dark:divide-red-900/20">
        {error && <p className="px-6 py-2 text-xs text-red-500 bg-red-50">{error}</p>}

        {/* Clear Jobs */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-navy-800 dark:text-white">Clear All Jobs</p>
              <p className="text-xs text-gray-400 mt-0.5">Delete all jobs, timers, outputs and dispatch records</p>
            </div>
            <button onClick={() => { setShowClearJobs(!showClearJobs); setError('') }}
              className="text-xs px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
              Clear Jobs
            </button>
          </div>
          {showClearJobs && (
            <div className="mt-3 flex items-center gap-3">
              <input type="password" placeholder="Confirm with your password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="input text-sm flex-1" />
              <button onClick={handleClearJobs} disabled={clearing}
                className="text-xs px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg">
                {clearing ? 'Clearing...' : 'Confirm'}
              </button>
            </div>
          )}
        </div>

        {/* Clear Staff */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-navy-800 dark:text-white">Clear All Staff</p>
              <p className="text-xs text-gray-400 mt-0.5">Delete all staff accounts except admin</p>
            </div>
            <button onClick={() => { setShowClearStaff(!showClearStaff); setError('') }}
              className="text-xs px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
              Clear Staff
            </button>
          </div>
          {showClearStaff && (
            <div className="mt-3 flex items-center gap-3">
              <input type="password" placeholder="Confirm with your password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="input text-sm flex-1" />
              <button onClick={handleClearStaff} disabled={clearing}
                className="text-xs px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg">
                {clearing ? 'Clearing...' : 'Confirm'}
              </button>
            </div>
          )}
        </div>

        {/* Clear Clients */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-navy-800 dark:text-white">Clear All Clients</p>
              <p className="text-xs text-gray-400 mt-0.5">Delete all client accounts and their data</p>
            </div>
            <button onClick={() => { setShowClearClients(!showClearClients); setError('') }}
              className="text-xs px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
              Clear Clients
            </button>
          </div>
          {showClearClients && (
            <div className="mt-3 flex items-center gap-3">
              <input type="password" placeholder="Confirm with your password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="input text-sm flex-1" />
              <button onClick={handleClearClients} disabled={clearing}
                className="text-xs px-3 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg">
                {clearing ? 'Clearing...' : 'Confirm'}
              </button>
            </div>
          )}
        </div>

        {/* Delete Inactive Clients */}
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-navy-800 dark:text-white">Clear Inactive Clients</p>
              <p className="text-xs text-gray-400 mt-0.5">Delete individual inactive client accounts</p>
            </div>
            <button onClick={() => { setShowInactiveClients(!showInactiveClients); fetchInactiveClients() }}
              className="text-xs px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors">
              View Inactive
            </button>
          </div>
          {showInactiveClients && (
            <div className="mt-3 space-y-2">
              {inactiveClients.length === 0 ? (
                <p className="text-xs text-gray-400">No inactive clients found.</p>
              ) : inactiveClients.map(client => (
                <div key={client.userId} className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-xl">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-navy-800 dark:text-white">{client.name}</p>
                    <p className="text-xs text-gray-400">{client.email}</p>
                  </div>
                  <input type="password" placeholder="Password" value={passwordInputs[client.userId] || ''}
                    onChange={e => setPasswordInputs(prev => ({ ...prev, [client.userId]: e.target.value }))}
                    className="input text-sm w-36" />
                  <button onClick={() => handleDeleteInactiveClient(client.userId)}
                    className="text-xs px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700">
                    Delete
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function MasterMailSection() {
  const [account, setAccount] = useState<any>(null)
  const [clients, setClients] = useState<any[]>([])
  const [domains, setDomains] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [newDomain, setNewDomain] = useState({ domain: '', clientId: '' })
  const [addingDomain, setAddingDomain] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState({
    email: '', imapHost: '', imapPort: '993',
    imapUser: '', imapPassword: '', useSsl: true, mailbox: 'INBOX'
  })
  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))

  const fetchAll = async () => {
    try {
      const [accRes, domRes, clientRes] = await Promise.all([
        api.get('/master-mail/account'),
        api.get('/master-mail/domains'),
        api.get('/clients/basic')
      ])
      if (accRes.data) {
        setAccount(accRes.data)
        setForm(f => ({ ...f,
          email: accRes.data.email,
          imapHost: accRes.data.imapHost,
          imapPort: accRes.data.imapPort.toString(),
          imapUser: accRes.data.imapUser,
          useSsl: accRes.data.useSsl,
          mailbox: accRes.data.mailbox
        }))
      }
      setDomains(domRes.data)
      setClients(clientRes.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { fetchAll() }, [])

  const handleSave = async () => {
    setSaving(true); setError('')
    try {
      await api.post('/master-mail/account', form)
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
      fetchAll()
    } catch (e: any) { setError(e.response?.data?.error || 'Failed to save') }
    setSaving(false)
  }

  const handleToggle = async () => {
    try { await api.patch('/master-mail/account/toggle'); fetchAll() } catch {}
  }

  const handleAddDomain = async () => {
    if (!newDomain.domain || !newDomain.clientId) return
    setAddingDomain(true); setError('')
    try {
      await api.post('/master-mail/domains', newDomain)
      setNewDomain({ domain: '', clientId: '' })
      fetchAll()
    } catch (e: any) { setError(e.response?.data?.error || 'Failed to add domain') }
    setAddingDomain(false)
  }

  const handleDeleteDomain = async (id: string) => {
    if (!window.confirm('Remove this domain mapping?')) return
    try { await api.delete(`/master-mail/domains/${id}`); fetchAll() } catch {}
  }

  const handleToggleDomain = async (id: string) => {
    try { await api.patch(`/master-mail/domains/${id}/toggle`); fetchAll() } catch {}
  }

  if (loading) return null

  return (
    <div className="card overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-purple-50 w-8 h-8 rounded-lg flex items-center justify-center">
            <Mail size={15} className="text-purple-500" />
          </div>
          <div>
            <p className="font-display font-semibold text-navy-900">Master Mail Account</p>
            <p className="text-xs text-gray-400">Single inbox that auto-routes emails to clients by sender domain</p>
          </div>
        </div>
        {account && (
          <button onClick={handleToggle}
            className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${account.isActive ? 'bg-green-50 text-green-600 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
            {account.isActive ? '● Active' : '○ Inactive'}
          </button>
        )}
      </div>
      <div className="p-6 space-y-6">
        <div className="space-y-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">IMAP Configuration</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className="label">Email Address</label><input className="input text-sm" placeholder="jobs@inkcrediblestudios.com" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div><label className="label">Mailbox</label><input className="input text-sm" placeholder="INBOX" value={form.mailbox} onChange={e => set('mailbox', e.target.value)} /></div>
            <div><label className="label">IMAP Host</label><input className="input text-sm" placeholder="mail.inkcrediblestudios.com" value={form.imapHost} onChange={e => set('imapHost', e.target.value)} /></div>
            <div><label className="label">IMAP Port</label><input className="input text-sm" type="number" placeholder="993" value={form.imapPort} onChange={e => set('imapPort', e.target.value)} /></div>
            <div><label className="label">Username</label><input className="input text-sm" placeholder="jobs@inkcrediblestudios.com" value={form.imapUser} onChange={e => set('imapUser', e.target.value)} /></div>
            <div>
              <label className="label">Password</label>
              <div className="relative">
                <input className="input text-sm pr-10" type={showPassword ? 'text' : 'password'} placeholder="App password" value={form.imapPassword} onChange={e => set('imapPassword', e.target.value)} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showPassword
                  ? <EyeOff size={14} />
                  : <Eye size={14} />
  }
</button>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-6">
              <Toggle value={form.useSsl} onChange={v => set('useSsl', v)} />
              <span className="text-sm text-navy-700">Use SSL/TLS</span>
            </div>
          </div>
          {error && <p className="text-xs text-red-500 bg-red-50 px-3 py-2 rounded-lg">{error}</p>}
          <button onClick={handleSave} disabled={saving} className="text-xs bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 disabled:opacity-50 transition-colors">
            {saving ? 'Saving...' : saved ? '✓ Saved' : account ? 'Update Account' : 'Save Account'}
          </button>
        </div>
        <div className="space-y-3 border-t border-gray-100 pt-6">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Client Domain Mapping</p>
          <div className="flex items-center gap-3 flex-wrap">
            <input className="input text-sm flex-1 min-w-0" placeholder="acmecorp.com" value={newDomain.domain} onChange={e => setNewDomain(d => ({ ...d, domain: e.target.value }))} />
            <select className="input text-sm flex-1 min-w-0" value={newDomain.clientId} onChange={e => setNewDomain(d => ({ ...d, clientId: e.target.value }))}>
              <option value="">Select client...</option>
              {clients.map((c: any) => <option key={c.clientId} value={c.clientId}>{c.companyName || c.name}</option>)}
            </select>
            <button onClick={handleAddDomain} disabled={addingDomain || !newDomain.domain || !newDomain.clientId}
              className="text-xs bg-navy-800 text-white px-4 py-2 rounded-lg hover:bg-navy-700 disabled:opacity-50 flex items-center gap-1 flex-shrink-0">
              <Plus size={12} /> Add
            </button>
          </div>
          {domains.length === 0 ? (
            <p className="text-xs text-gray-400 py-3 text-center">No domains mapped yet</p>
          ) : (
            <div className="divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
              {domains.map((d: any) => (
                <div key={d.id} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${d.isActive ? 'bg-green-400' : 'bg-gray-300'}`} />
                    <div>
                      <p className="text-sm font-mono font-medium text-navy-800">@{d.domain}</p>
                      <p className="text-xs text-gray-400">{d.clientName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleToggleDomain(d.id)} className={`text-xs px-2 py-1 rounded-lg font-medium ${d.isActive ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>{d.isActive ? 'Active' : 'Inactive'}</button>
                    <button onClick={() => handleDeleteDomain(d.id)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
